import {
  ListenCounter,
  STEPS,
  currentStep,
  findSegmentIndex,
  markStep,
  mergeStepState,
  tokenize,
  type StepId,
  type StepState,
  type SubtitleMode,
} from '@engpod/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Dock } from '../components/Dock';
import { Icon } from '../components/Icon';
import { Player, type PlayerApi } from '../components/Player';
import { WordSheet } from '../components/WordSheet';
import { ApiError, api, apiBase, cached, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { clock, speak } from '../lib/format';
import { prefs, readLocal, writeLocal } from '../lib/local';
import { Expressions } from '../steps/Expressions';
import { Gist } from '../steps/Gist';
import { Intensive } from '../steps/Intensive';
import { Output } from '../steps/Output';
import { Shadowing } from '../steps/Shadowing';
import type { StepProps } from '../steps/context';
import type { EpisodeDetail, EpisodeSummary, Program, Progress, SegmentView } from '../types';

const RATES = [1, 1.25, 1.5, 0.75, 0.85];
const VIDEO_MODES: [SubtitleMode, string][] = [
  ['en', '英文'],
  ['both', '中英文'],
  ['zh', '中文'],
  ['none', '无字幕'],
];
type TextMode = 'both' | 'en' | 'zh' | 'hidden';
const TEXT_MODES: [TextMode, string][] = [
  ['both', '全部'],
  ['en', '英语'],
  ['zh', '中文'],
  ['hidden', '隐藏'],
];
const STEP_VIEWS: Record<StepId, (p: StepProps) => React.JSX.Element> = {
  gist: Gist,
  intensive: Intensive,
  expressions: Expressions,
  shadowing: Shadowing,
  output: Output,
};
const BARS = Array.from({ length: 64 }, (_, i) => 20 + ((i * 17) % 19));

interface Group {
  id: string;
  title: string;
  level?: string | null;
  episodes: EpisodeSummary[];
}

const LEVEL_NAMES: Record<string, string> = { 'A1–A2': '入门', A2: '入门', B1: '中级', B2: '中高级' };
const finished = (id: string) => STEPS.every((s) => readLocal(id).stepState.steps[s.id]?.done);

/**
 * Which episode a programme opens on: the one you were in the middle of, otherwise its newest one.
 * (Episodes arrive newest first.)
 */
function entryEpisode(g: Group): EpisodeSummary | undefined {
  const open = g.episodes.filter((e) => !e.locked);
  const last = open.find((e) => e.id === prefs.get<string>(`episode.${g.id}`, ''));
  return last && !finished(last.id) ? last : (open[0] ?? g.episodes[0]);
}

export function LearnPage() {
  const { me } = useAuth();
  const loggedIn = Boolean(me?.user);
  const [params, setParams] = useSearchParams();
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [library, setLibrary] = useState<EpisodeSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setError(null);
    const { snapshot, fresh } = cached<{ programs: Program[] }>('/programs');
    if (snapshot) setPrograms(snapshot.programs);
    fresh.then(({ data, changed }) => changed && setPrograms(data.programs), (e) => !snapshot && setError(errorText(e)));
  }, [me?.user?.id, attempt]);
  useEffect(() => {
    if (!loggedIn) return setLibrary([]);
    const { snapshot, fresh } = cached<{ episodes: (EpisodeSummary & { segments: number })[] }>('/library');
    const show = (d: { episodes: (EpisodeSummary & { segments: number })[] }) => setLibrary(d.episodes.filter((e) => e.segments > 0));
    if (snapshot) show(snapshot);
    fresh.then(({ data, changed }) => changed && show(data), () => !snapshot && setLibrary([]));
  }, [loggedIn]);

  const groups = useMemo<Group[]>(() => {
    const list: Group[] = (programs ?? []).map((p) => ({ id: p.id, title: p.title, level: p.level, episodes: p.episodes }));
    if (library.length) list.push({ id: 'mine', title: '我的资料', episodes: library });
    return list;
  }, [programs, library]);

  const all = groups.flatMap((g) => g.episodes.map((e) => ({ ...e, group: g.id })));
  const requested = params.get('ep');
  // Opening the app: an episode you are still working through wins; otherwise the newest
  // episode of the programme at your chosen level (我的 → 学习设置 → 默认节目).
  const lastGroup = groups.find((g) => g.id === prefs.get<string>('program', ''));
  const lastEp = lastGroup?.episodes.find((e) => e.id === prefs.get<string>(`episode.${lastGroup.id}`, ''));
  const homeGroup = groups.find((g) => g.id === prefs.get<string>('homeProgram', 'bbc-easy')) ?? groups[0];
  const entry = (lastEp && !lastEp.locked && !finished(lastEp.id) ? lastEp : undefined) ?? (homeGroup && entryEpisode(homeGroup));
  const selected =
    all.find((e) => e.id === requested) ??
    all.find((e) => e.id === entry?.id) ??
    all.find((e) => !e.locked);
  const group = groups.find((g) => g.id === selected?.group);

  const choose = (id: string) => {
    const ep = all.find((e) => e.id === id);
    if (ep) {
      prefs.set('program', ep.group);
      prefs.set(`episode.${ep.group}`, ep.id);
    }
    setParams({ ep: id });
  };

  if (error) return <ConnectError message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!programs) return <p className="page muted">加载中…</p>;
  if (!selected || !group) return <p className="page muted">还没有可学习的内容。</p>;

  return (
    <EpisodeView
      key={selected.id}
      episodeId={selected.id}
      group={group}
      groups={groups}
      onChoose={choose}
      startAtMs={Number(params.get('t')) || undefined}
    />
  );
}

function EpisodeView({ episodeId, group, groups, onChoose, startAtMs }: { episodeId: string; group: Group; groups: Group[]; onChoose(id: string): void; startAtMs?: number }) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const loggedIn = Boolean(me?.user);
  const [detail, setDetail] = useState<EpisodeDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [state, setState] = useState<StepState>(() => readLocal(episodeId).stepState);
  const [listenCount, setListenCount] = useState(0);
  const [openStep, setOpenStep] = useState<StepId | null>(null);
  const [tab, setTab] = useState<'subtitles' | 'expressions'>('subtitles');
  const [videoMode, setVideoModeState] = useState<SubtitleMode>(() => prefs.get<SubtitleMode>('subtitle', 'both'));
  const [textMode, setTextMode] = useState<TextMode>(() => prefs.get<TextMode>('textMode', 'both'));
  const [follow, setFollowState] = useState(() => prefs.get<boolean>('follow', false));
  const [current, setCurrent] = useState(-1);
  const [timeMs, setTimeMs] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(() => prefs.get<number>('rate', 1));
  const [loopCount, setLoopCount] = useState(1);
  const [audioOnly, setAudioOnlyState] = useState(() => prefs.get<boolean>('audioOnly', false));
  const setAudioOnly = (v: boolean) => {
    setAudioOnlyState(v);
    prefs.set('audioOnly', v);
  };
  const [gesture, setGesture] = useState<{ id: string; mode: 'loop' | 'once' } | null>(null);
  const [word, setWord] = useState<{ seg: SegmentView; token: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reads, setReads] = useState(() => prefs.get<number>(`reads.${episodeId}.${new Date().toISOString().slice(0, 10)}`, 0));
  const [speaking, setSpeaking] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const player = useRef<PlayerApi>(null);
  const stopAt = useRef<{ start: number; end: number; loop: boolean } | null>(null);
  const lastIdx = useRef(-1);
  const lastUi = useRef(0);
  const lastSaved = useRef(0);
  const transcript = useRef<HTMLDivElement>(null);
  const local = useRef(readLocal(episodeId));

  useEffect(() => {
    const ep = cached<EpisodeDetail>(`/episodes/${episodeId}`);
    if (ep.snapshot) setDetail(ep.snapshot);
    ep.fresh.then(
      ({ data, changed }) => changed && setDetail(data),
      (e) => !ep.snapshot && setError(e instanceof ApiError ? e : new ApiError(0, 'error', errorText(e))),
    );
    if (loggedIn) {
      const apply = ({ progress }: { progress: Progress[] }) => {
        const p = progress.find((x) => x.episodeId === episodeId);
        if (!p) return;
        setListenCount(p.listenCount);
        setState((s) => mergeStepState(s, p.stepState));
      };
      const pr = cached<{ progress: Progress[] }>('/progress');
      if (pr.snapshot) apply(pr.snapshot);
      pr.fresh.then(({ data }) => apply(data), () => undefined);
    }
  }, [episodeId, loggedIn]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (speaking === null) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [speaking]);

  const sync = useCallback(
    (patch: { positionMs?: number; stepState?: StepState; listenIncrement?: number }) => {
      writeLocal(episodeId, patch);
      if (loggedIn) void api(`/progress/${episodeId}`, { method: 'PUT', json: patch }).catch(() => undefined);
    },
    [episodeId, loggedIn],
  );

  const update = useCallback(
    (patch: Partial<StepState>) =>
      setState((s) => {
        const next = { ...s, ...patch };
        sync({ stepState: next });
        return next;
      }),
    [sync],
  );

  const complete = useCallback(
    (id: StepId) =>
      setState((s) => {
        const next = markStep(s, id);
        sync({ stepState: next });
        // Move forward to the next unfinished step; after the last one, stay so its summary shows.
        const order = STEPS.map((x) => x.id);
        const upcoming = order.slice(order.indexOf(id) + 1).find((x) => !next.steps[x]?.done) ?? id;
        if (upcoming === 'intensive') {
          // 精听 lives on its own page.
          setOpenStep(null);
          setTimeout(() => navigate(`/listen/${episodeId}`), 300);
        } else setOpenStep(upcoming);
        return next;
      }),
    [sync, navigate, episodeId],
  );

  const counter = useMemo(
    () =>
      new ListenCounter(() => {
        setListenCount((n) => n + 1);
        sync({ listenIncrement: 1 });
      }),
    [sync],
  );

  const segments = detail?.segments ?? [];
  const onTick = useCallback(
    (sec: number) => {
      const ms = sec * 1000;
      const stop = stopAt.current;
      if (stop && ms >= stop.end) {
        if (stop.loop) {
          player.current?.seek(stop.start / 1000);
          setLoopCount((n) => n + 1);
        }
        else {
          player.current?.pause();
          stopAt.current = null;
          setGesture(null);
        }
      }
      const i = findSegmentIndex(segments, ms);
      if (i !== -1 && i !== lastIdx.current) {
        lastIdx.current = i;
        setCurrent(i);
      }
      const t = performance.now();
      if (t - lastUi.current > 250) {
        lastUi.current = t;
        setTimeMs(ms);
      }
      counter.update(sec, player.current?.duration() ?? 0);
      if (t - lastSaved.current > 10_000 && ms > 0) {
        lastSaved.current = t;
        sync({ positionMs: Math.round(ms) });
      }
    },
    [segments, counter, sync],
  );

  // 跟随播放 (off by default): keep the playing sentence in view, but only while the
  // subtitle list is on screen, so pressing play never drags the video out of view.
  useEffect(() => {
    if (!follow || !playing || current < 0 || tab !== 'subtitles') return;
    const list = transcript.current?.getBoundingClientRect();
    if (!list || list.top > window.innerHeight * 0.6) return;
    transcript.current?.querySelector(`[data-idx="${current}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [current, follow, playing, tab]);

  const playSegment = useCallback((seg: Pick<SegmentView, 'id' | 'startMs' | 'endMs'>, loop = false) => {
    setLoopCount(1);
    stopAt.current = { start: seg.startMs, end: seg.endMs + 120, loop };
    setGesture(loop ? { id: seg.id, mode: 'loop' } : null);
    player.current?.seek(seg.startMs / 1000);
    player.current?.play();
  }, []);

  /** Tapping a sentence cycles: loop → play once → stop (prototype gesture). */
  const tapSentence = (seg: SegmentView) => {
    if (gesture?.id !== seg.id) return playSegment(seg, true);
    if (gesture.mode === 'loop') {
      stopAt.current = { start: seg.startMs, end: seg.endMs + 120, loop: false };
      setGesture({ id: seg.id, mode: 'once' });
      player.current?.seek(seg.startMs / 1000);
      player.current?.play();
      return;
    }
    stopAt.current = null;
    setGesture(null);
    player.current?.pause();
  };

  const freePlay = () => {
    stopAt.current = null;
    setGesture(null);
  };
  const mediaKind = useRef<string | null>(null);
  mediaKind.current = detail?.episode.media.kind ?? null;
  const togglePlay = useCallback(() => {
    // Locked or missing media renders cover art without a player: say why instead of doing nothing.
    if (!player.current) {
      if (mediaKind.current === 'locked') {
        setToast('请先到「我的」输入口令登录，登录后即可播放');
        setTimeout(() => navigate('/me'), 1200);
      } else setToast('这一集的视频还没有准备好');
      return;
    }
    if (player.current.paused()) {
      stopAt.current = null;
      setGesture(null);
      player.current.play();
    } else player.current?.pause();
  }, [navigate]);
  const setMode = useCallback((m: SubtitleMode) => setVideoModeState(m), []);
  const setRate = useCallback((r: number) => {
    setRateState(r);
    player.current?.setRate(r);
  }, []);
  const openWord = useCallback((seg: SegmentView, token: number) => {
    player.current?.pause();
    setWord({ seg, token });
  }, []);
  const openStepSheet = (id: StepId) => {
    // 精听 has its own full page.
    if (id === 'intensive') {
      player.current?.pause();
      return navigate(`/listen/${episodeId}`);
    }
    const closing = openStep === id;
    setOpenStep(closing ? null : id);
    if (!closing && id === 'gist') {
      // Listening steps happen on the video: go back to it with captions off.
      setVideoModeState('none');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  // Leaving the gist/intensive sheet brings back the learner's own subtitle choice.
  useEffect(() => {
    if (openStep !== 'gist' && openStep !== 'intensive') setVideoModeState(prefs.get<SubtitleMode>('subtitle', 'both'));
  }, [openStep]);

  const toggleHard = async (seg: SegmentView) => {
    const hard = new Set(state.hardSegments ?? []);
    if (hard.has(seg.id)) hard.delete(seg.id);
    else hard.add(seg.id);
    update({ hardSegments: [...hard] });
    setToast(hard.has(seg.id) ? '已标记为难句，跟读时会优先练习' : '已取消难句标记');
  };

  const countRead = () => {
    const key = `reads.${episodeId}.${new Date().toISOString().slice(0, 10)}`;
    const n = reads + 1;
    prefs.set(key, n);
    setReads(n);
  };
  const toggleSpeaking = () => {
    if (speaking === null) {
      setSpeaking(Date.now());
      setNow(Date.now());
      return;
    }
    const secs = Math.round((Date.now() - speaking) / 1000);
    setSpeaking(null);
    setToast(secs < 3 ? '再多说一点吧' : `这次说了 ${secs} 秒英语，很棒`);
  };

  if (error) {
    return (
      <div className="listening-home">
        <Header onToast={setToast} onQuote={() => undefined} />
        <div className="home-main">
          <Categories groups={groups} group={group} onChoose={onChoose} />
          <div className="card">
            <p>{error.message}</p>
            {error.code === 'member_only' && (
              <p className="muted">
                免费版可学习最近 4 周的节目。<Link to="/me">查看会员</Link>
              </p>
            )}
          </div>
        </div>
        <Dock />
      </div>
    );
  }
  if (!detail) return <p className="page muted">加载中…</p>;

  const ep = detail.episode;
  const seg = current >= 0 ? (segments[current] ?? null) : null;
  const durMs = (durationSec || (ep.durationMs ?? 0) / 1000) * 1000;
  const played = durMs ? Math.min(1, timeMs / durMs) : 0;
  const startAt = startAtMs ? startAtMs / 1000 : local.current.positionMs / 1000;
  const idx = group.episodes.findIndex((e) => e.id === ep.id);
  const hard = new Set(state.hardSegments ?? []);
  const StepView = openStep ? STEP_VIEWS[openStep] : null;
  const stepProps: StepProps = { detail, state, listened: listenCount > 0, current, update, complete, playSegment, setMode, openWord, setRate, rate, loggedIn, togglePlay, playing, loopCount };
  const done = Object.fromEntries(STEPS.map((s) => [s.id, Boolean(state.steps[s.id]?.done)]));
  const captionEn = seg && (videoMode === 'en' || videoMode === 'both') ? seg.en : '';
  const captionZh = seg && (videoMode === 'zh' || videoMode === 'both') ? seg.zh : '';

  return (
    <div className="listening-home">
      <Header
        onToast={setToast}
        onQuote={() => {
          setTab('expressions');
          document.getElementById('home-learning')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
      <main className="home-main">
        <Categories groups={groups} group={group} onChoose={onChoose} />
        <div className="episode-switcher">
          <button className="plain icon" aria-label="上一集" disabled={idx >= group.episodes.length - 1} onClick={() => onChoose(group.episodes[idx + 1]!.id)}>
            ‹
          </button>
          <div className="episode-select">
            <span className="episode-pill">{ep.publishedOn ? ep.publishedOn.slice(5, 10) : `EP.${String(idx + 1).padStart(2, '0')}`}⌄</span>
            <span className="episode-name">{ep.title}</span>
            <select value={ep.id} onChange={(e) => onChoose(e.target.value)} aria-label="选择本系列视频">
              {group.episodes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.locked ? '🔒 ' : ''}
                  {e.publishedOn ?? ''} · {e.title}
                </option>
              ))}
            </select>
          </div>
          <button className="plain icon" aria-label="下一集" disabled={idx <= 0} onClick={() => onChoose(group.episodes[idx - 1]!.id)}>
            ›
          </button>
        </div>

        <section className="video-stage" aria-label="16:9 视频与播客播放器">
          <Player
            ref={player}
            media={ep.media}
            mediaType={ep.mediaType}
            poster={ep.posterUrl}
            title={ep.title}
            programTitle={group.title}
            startAt={startAt}
            onTick={onTick}
            onDuration={(d) => {
              setDurationSec(d);
              player.current?.setRate(rate);
            }}
            onPlayState={setPlaying}
            audioOnly={audioOnly}
            onRequestAudio={() => setAudioOnly(true)}
          />
          <div className="stage-caption" hidden={!captionEn && !captionZh}>
            {captionEn && <span className="caption-en">{captionEn}</span>}
            {captionZh && <span className="caption-zh">{captionZh}</span>}
          </div>
        </section>

        <div className="voice-row">
          <div className="video-subtitle-switch" role="group" aria-label="视频字幕显示">
            {VIDEO_MODES.map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                aria-pressed={videoMode === mode}
                className={videoMode === mode ? 'active' : ''}
                onClick={() => {
                  setVideoModeState(mode);
                  prefs.set('subtitle', mode);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <section className="home-playback">
          <div className="wave-track">
            <div className="wave-bars" aria-hidden="true">
              {BARS.map((h, i) => (
                <i key={i} className={i / BARS.length < played ? 'played' : ''} style={{ height: h }} />
              ))}
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(1, durMs)}
              value={Math.min(timeMs, durMs)}
              step={100}
              aria-label="播放进度"
              onChange={(e) => {
                freePlay();
                player.current?.seek(Number(e.target.value) / 1000);
              }}
            />
          </div>
          <div className="home-time">
            <time>{clock(timeMs)}</time>
            <time>-{clock(Math.max(0, durMs - timeMs))}</time>
          </div>
          <div className="large-transport">
            <button className="skip-control" aria-label="后退 15 秒" onClick={() => player.current?.seek((player.current?.time() ?? 0) - 15)}>
              <Icon name="back" />
              <span>15</span>
            </button>
            <button className="big-play" aria-label={playing ? '暂停' : '播放'} onClick={togglePlay}>
              <Icon name={playing ? 'pause' : 'play'} />
            </button>
            <button className="skip-control" aria-label="前进 15 秒" onClick={() => player.current?.seek((player.current?.time() ?? 0) + 15)}>
              <Icon name="forward" />
              <span>15</span>
            </button>
          </div>
          <div className="player-extras">
            <button
              onClick={() => {
                freePlay();
                player.current?.seek(0);
                player.current?.play();
              }}
            >
              从头开始
            </button>
            <span />
            <button onClick={() => setRate(RATES[(RATES.indexOf(rate) + 1) % RATES.length]!)}>{rate}×</button>
            {ep.media.kind === 'file' && ep.media.audioUrl && ep.mediaType === 'video' && (
              <>
                <span />
                <div className="media-mode" role="group" aria-label="播放视频或仅音频">
                  <button className={audioOnly ? '' : 'active'} onClick={() => setAudioOnly(false)}>
                    视频
                  </button>
                  <button className={audioOnly ? 'active' : ''} onClick={() => setAudioOnly(true)}>
                    音频
                  </button>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="home-learning" id="home-learning">
          <div className="tabs">
            <button className={tab === 'subtitles' ? 'active' : ''} onClick={() => setTab('subtitles')}>
              字幕
            </button>
            <button className={tab === 'expressions' ? 'active' : ''} onClick={() => setTab('expressions')}>
              核心表达
            </button>
          </div>
          {tab === 'subtitles' ? (
            <>
              <div className="sub-controls row spread wrap">
                <div className="segmented">
                  {TEXT_MODES.map(([m, label]) => (
                    <button
                      key={m}
                      className={textMode === m ? 'active' : ''}
                      onClick={() => {
                        setTextMode(m);
                        prefs.set('textMode', m);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="row">
                  <button
                    className="small plain"
                    onClick={() => {
                      setFollowState(!follow);
                      prefs.set('follow', !follow);
                    }}
                  >
                    {follow ? '跟随播放' : '自由浏览'}
                  </button>
                  {me?.user?.role === 'admin' && !ep.own && (
                    <button className="small plain" title="校订字幕" onClick={() => navigate(`/admin/${ep.id}`)}>
                      <Icon name="settings" />
                    </button>
                  )}
                </div>
              </div>
              <div className="transcript" ref={transcript}>
                {segments.map((s) => (
                  <article
                    key={s.id}
                    data-idx={s.idx}
                    className={`sentence${s.idx === current ? ' active' : ''}${gesture?.id === s.id ? ` gesture-${gesture.mode}` : ''}`}
                    onClick={() => tapSentence(s)}
                  >
                    <div className="sentence-top">
                      <button className="plain stamp" style={{ padding: 0 }} onClick={(e) => (e.stopPropagation(), freePlay(), player.current?.seek(s.startMs / 1000), player.current?.play())}>
                        {clock(s.startMs)}
                      </button>
                      <div className="tools">
                        <button title="听这一句" onClick={(e) => (e.stopPropagation(), playSegment(s))}>
                          <Icon name="volume" />
                        </button>
                        <button title="标记难句" className={hard.has(s.id) ? 'marked' : ''} onClick={(e) => (e.stopPropagation(), void toggleHard(s))}>
                          <Icon name="bookmark" />
                        </button>
                      </div>
                    </div>
                    {textMode === 'hidden' ? (
                      <p className="en muted">先听一听，让耳朵走在前面。</p>
                    ) : (
                      <>
                        {textMode !== 'zh' && (
                          <p className="en">
                            {tokenize(s.en).map((t, i) =>
                              t.word ? (
                                <span key={i} className="word" onClick={(e) => (e.stopPropagation(), openWord(s, i))}>
                                  {t.text}
                                </span>
                              ) : (
                                <span key={i}>{t.text}</span>
                              ),
                            )}
                          </p>
                        )}
                        {textMode !== 'en' && <p className="zh">{s.zh || '此句还没有中文。'}</p>}
                      </>
                    )}
                  </article>
                ))}
              </div>
              <div className="learning-hint">
                <Icon name="spark" />
                点单词查词 · 点时间从这句播放 · 点句子单句循环
              </div>
              <div className="source-note">
                {ep.translationOrigin ? `中文：${ep.translationOrigin}。` : ''}
                {ep.programId && ep.sourceUrl ? (
                  <>
                    内容来源{' '}
                    <a href={ep.sourceUrl} target="_blank" rel="noreferrer">
                      BBC Learning English
                    </a>
                    ，版权归 BBC 所有，本应用仅提供学习工具。
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <ExpressionList detail={detail} playSegment={playSegment} loggedIn={loggedIn} onToast={setToast} />
          )}
        </section>
      </main>

      <aside className="floating-shadow">
        <button className="read-counter" aria-label="读完一句，记录一次" onClick={countRead}>
          {reads}
        </button>
        <button aria-label={speaking === null ? '开始口语计时' : '结束口语计时'} className={speaking === null ? '' : 'speaking'} onClick={toggleSpeaking}>
          {speaking === null ? <Icon name="mic" /> : Math.floor((now - speaking) / 1000)}
        </button>
        <span>{speaking === null ? '读完一句后，点按记录' : '正在计时，说完再点一次'}</span>
      </aside>

      {openStep && StepView && (
        <section className={`step-sheet${openStep === 'gist' ? ' compact' : ''}`} aria-label={STEPS.find((s) => s.id === openStep)?.label}>
          <header>
            <div>
              <h3>{STEPS.find((s) => s.id === openStep)?.label}</h3>
              <small>{STEPS.find((s) => s.id === openStep)?.hint}</small>
            </div>
            <div className="row tight">
              <button className="round-mini" aria-label={playing ? '暂停' : '播放'} onClick={togglePlay}>
                <Icon name={playing ? 'pause' : 'play'} />
              </button>
              <button className="round-mini plain-mini" aria-label="收起" onClick={() => setOpenStep(null)}>
                <Icon name="close" />
              </button>
            </div>
          </header>
          <div className="step-sheet-body">
            <StepView {...stepProps} />
          </div>
        </section>
      )}

      <Dock steps={{ done, open: openStep, onStep: openStepSheet }} />

      {word && <WordSheet segment={word.seg} tokenIndex={word.token} episode={ep} programTitle={group.title} onPlay={() => playSegment(word.seg)} onClose={() => setWord(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function Header({ onToast, onQuote }: { onToast(t: string): void; onQuote(): void }) {
  return (
    <header className="home-header">
      <Link className="brand" to="/">
        ENGPOD
      </Link>
      <div className="home-tools">
        <button className="language-pill" aria-label="界面语言" onClick={() => onToast('界面语言目前只有简体中文')}>
          <Icon name="globe" />
          简体中文
        </button>
        <Link className="round-tool favorite-tool" to="/vocab" aria-label="生词本">
          <Icon name="heart" />
        </Link>
        <button className="round-tool" aria-label="本集核心表达" onClick={onQuote}>
          <Icon name="quote" />
        </button>
        <Link className="round-tool" to="/me" aria-label="设置与账号">
          <Icon name="settings" />
        </Link>
      </div>
    </header>
  );
}

function Categories({ groups, group, onChoose }: { groups: Group[]; group: Group; onChoose(id: string): void }) {
  return (
    <nav className="home-categories" aria-label="学习系列">
      {groups.map((g) => (
        <button
          key={g.id}
          aria-pressed={g.id === group.id}
          className={g.id === group.id ? 'active' : ''}
          disabled={!g.episodes.length}
          onClick={() => {
            const first = entryEpisode(g);
            if (first) onChoose(first.id);
          }}
        >
          {g.title}
          <small>
            {g.level && <span className="level-tag">{`${g.level} ${LEVEL_NAMES[g.level] ?? ''}`.trim()}</span>}
            {g.episodes.length} {g.id === 'mine' ? '个内容' : '期'}
          </small>
        </button>
      ))}
    </nav>
  );
}

function ExpressionList({ detail, playSegment, loggedIn, onToast }: { detail: EpisodeDetail; playSegment(s: SegmentView): void; loggedIn: boolean; onToast(t: string): void }) {
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const bySeg = new Map(detail.segments.map((s) => [s.id, s]));
  const list = detail.lesson.expressions;
  if (!list.length) {
    return (
      <div className="empty">
        <h3>这一集还没有整理核心表达</h3>
        <p className="muted">在字幕里点单词，或选一段词组加入表达本。</p>
      </div>
    );
  }
  const save = async (id: string, text: string, meaning: string | null, segmentId: string) => {
    if (!loggedIn) return onToast('登录后可以收藏');
    try {
      await api('/vocab', { method: 'POST', json: { kind: 'expression', surface: text, meaning: meaning ?? undefined, segmentId } });
      setSaved((s) => new Set(s).add(id));
      onToast('已收藏到生词本，连同原句和原声');
    } catch (e) {
      onToast(errorText(e));
    }
  };
  return (
    <>
      {list.map((x, i) => {
        const seg = x.segmentId ? bySeg.get(x.segmentId) : undefined;
        return (
          <article key={x.id} className="expression-lesson">
            <div className="expression-illustration">
              <span>EXPRESSION {String(i + 1).padStart(2, '0')}</span>
              <strong>{x.text}</strong>
              <small>{x.meaning}</small>
            </div>
            <h3>
              <button className="plain" aria-label="听原句" onClick={() => seg && playSegment(seg)}>
                <Icon name="volume" />
              </button>
              {x.text}
            </h3>
            <p>{x.meaning}</p>
            {seg && (
              <div className="example-line original" onClick={() => playSegment(seg)}>
                <button className="icon plain" aria-label="播放原句">
                  <Icon name="play" />
                </button>
                <div>
                  <div>{seg.en}</div>
                  <p>{seg.zh}</p>
                </div>
              </div>
            )}
            <div className="explanation">{x.explanation || '试着把它用进自己的日常对话里。'}</div>
            {(x.examples ?? []).map((ex, j) => (
              <div key={j} className="example-line">
                <button className="icon plain" aria-label={`朗读例句 ${j + 1}`} onClick={() => speak(ex.en)}>
                  <Icon name="volume" />
                </button>
                <div>
                  <div>{ex.en}</div>
                  <p>{ex.zh}</p>
                </div>
              </div>
            ))}
            {seg && (
              <div className="row spread">
                <small className="muted">例句为辅助编写；原句来自本集原声。</small>
                <button className={saved.has(x.id) ? 'secondary' : 'primary'} disabled={saved.has(x.id)} onClick={() => save(x.id, x.text, x.meaning, seg.id)}>
                  {saved.has(x.id) ? '已收藏 ✓' : '收藏'}
                </button>
              </div>
            )}
          </article>
        );
      })}
    </>
  );
}

/** Shown when the app cannot reach the API (e.g. the phone is not on the Mac's Wi-Fi during local testing). */
function ConnectError({ message, onRetry }: { message: string; onRetry(): void }) {
  const server = apiBase() || location.origin;
  return (
    <div className="connect-error">
      <h2>连不上 ENGPOD 服务</h2>
      <p className="status">{message}</p>
      <ul>
        {/(localhost|\.local|192\.168\.|10\.\d)/.test(server) ? (
          <>
            <li>手机和电脑连同一个 Wi-Fi（不要用 5G / 4G 流量）</li>
            <li>电脑上运行着「启动ENGPOD.command」</li>
            <li>iPhone 设置 → 隐私与安全性 → 本地网络 → 打开 ENGPOD</li>
          </>
        ) : (
          <>
            <li>服务器在海外，部分运营商网络（尤其移动 5G / 4G）可能连不上或很慢</li>
            <li>试试切换到 Wi-Fi，或在 Wi-Fi 和 5G 之间切换</li>
            <li>临时办法：开启 VPN 后重试</li>
          </>
        )}
      </ul>
      <small className="muted">服务地址：{server}</small>
      <button className="primary" onClick={onRetry}>
        重试
      </button>
    </div>
  );
}
